<template>
  <form @submit.prevent="submit" :name="$options.name" novalidate>
    <template v-if="data.heading">
      <h3>{{data.heading.title}}</h3>
      <div v-html="data.heading.descriptionHtml"></div>
    </template>

    <div v-if="error && data.error" class="error-message">
      <div class="error-message__icon"></div>
      <div class="error-message__text">
        <h2>{{data.error.title}}</h2>
        <div v-html="data.error.descriptionHtml"></div>
      </div>
    </div>

    <Loading v-if="loading" />

    <template v-else>
      <Input
        v-for="field in fields"
        :key="field.name"
        :name="field.name"
        :type="isPasswordField(field) ? passwordFieldType : field.type"
        :label="field.label"
        :placeholder="field.label"
        :startingValue="field.startingValue"
        :required="true"
        :customValidation="field.customValidation"
        :vuexPath="vuexPath"
        :pattern="field.pattern"
        :title="field.title"
        :ref="`${field.name}`"
        :isPassword="isPasswordField(field)"
        :fieldType="passwordFieldType"
        @toggle-password="togglePasswordVisibility"
      />

      <button type="submit" class="btn" aria-label="Submit form" title="Submit form">{{data.submitLabel}}</button>

      <template v-if="data.links">
        <p class="form-links" v-for="link in data.links" :key="link.name">
          <span v-if="link.label">{{link.label}}</span>
          <a :href="link.url" :target="link.target">{{link.name}}</a>
        </p>
      </template>
    </template>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import Input from '@/vue/components/Form/Input.vue';
import Loading from '@/vue/components/Form/Loading.vue';

export default {
  components: {
    Input,
    Loading
  },

  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      vuexPath: ['account', 'form'], // the path at which the form data will be saved
      error: false,
      loading: false,
      passwordFieldType: 'password',
      hasPasswordField: false
    };
  },

  computed: {
    ...mapGetters(['formIsValid']),

    formData() {
      return this.$store.getters.formDataFlat(this.vuexPath);
    }
  },

  mounted() {
    this.hasPasswordField = this.fields.some((field) => field.type === 'password');
  },

  methods: {
    async submit() {
      // reset error state before submit
      this.error = false;

      // submit the form in vuex (shows errors if necessary)
      this.$store.commit('setHasBeenSubmitted', this.vuexPath);

      // ensure the form is valid
      await this.handleFormSubmit();
    },

    async handleFormSubmit() {
      if (this.formIsValid(this.vuexPath)) {
        // Set loading spinner while waiting for response
        this.loading = true;

        try {
          const response = await this.postData();
          var parsedData = this.parseResponseData(response);
          const originalEmail = parsedData.email;
          
          if (this.isLoginComponent()) {
            const userDataResponse = await this.fetchUserData();
            if(userDataResponse.data) {
              parsedData = userDataResponse.data;
              parsedData.email = originalEmail;
            }

            GTM.loginEvent(parsedData.clientId, parsedData.email);

          } 

          this.handleResponse(parsedData);
        } catch (error) {
          this.handleError();
        }
      }
    },

    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },

    isPasswordField(field) {
      return field.type === 'password' || field.type === 'confirmPassword';
    },

    postData() {
      return axios.post(this.data.submitUrl, this.formData);
    },

    parseResponseData(response) {
      return JSON.parse(response.config.data);
    },

    isLoginComponent() {
      return this.$options.name === 'Login';
    },

    fetchUserData() {
      if (this.data.userDataUrl) {
        return axios.get(this.data.userDataUrl);
      }

      if (this.data.userDetailsUrl) {
        return axios.get(this.data.userDetailsUrl);
      }
    },

    handleResponse(parsedData) {
      if (this.$options.name === 'Register') {
        this.handleRegistration(parsedData);
      } else {
        this.handleLogin(parsedData);
      }
    },

    handleRegistration(parsedData) {
      GTM.registrationEvent(null, parsedData.email);

      if (this.data.redirectUrl) {
        window.location.href = this.data.redirectUrl;
      } else {
        this.$emit('loginSuccess');
        this.loading = false;
      }
    },

    handleLogin(parsedData) {
      GTM.formSubmitEvent(this.$options.name);

      if (this.data.redirectUrl) {
        window.location.href = this.data.redirectUrl;
      } else {
        this.$emit('loginSuccess');
        this.loading = false;
      }
    },

    handleError() {
      localStorage.setItem('authToken', null);
      this.error = true;
      this.loading = false;
    }

  }
};
</script>